<template>
  <div>
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-form v-else @submit.prevent="onSubmit">
      <b-form-group v-for="field in fields"
        v-if="(field.inFormCondition && field.inFormCondition(form)) || !field.inFormCondition" :key="field.name"
        :label="field.label + (isFieldRequired(field) ? ' *' : '') + ' :'" :label-for="field.name"
        :description="field.description">
        <b-form-select v-if="field.type == 'select'" :id="field.name" v-model="form[field.name]"
          :options="field.options ? field.options($store, form) : options[field.name + '_options']"
          :required="field.required && !isRequiredWaived(field)"
          :disabled="(!field.options || !field.options($store, form).length) && (!options[field.name + '_options'] || !options[field.name + '_options'].length)"
          :multiple="field.multiple">
          <template slot="first">
            <option v-if="!options[field.name + '_options'] || !options[field.name + '_options'].length" :value="null"
              disabled>
              {{ field.firstOption ? field.firstOption : "Chargement ..." }}
            </option>
            <option v-else :value="null">-- Sélectionnez une option --</option>
          </template>
        </b-form-select>
        <Chosen v-else-if="field.type == 'chosen'" :id="field.name" v-model="form[field.name]"
          :options="options[field.name + '_options']"
          :disabled="!options[field.name + '_options'] || !options[field.name + '_options'].length"
          :multiple="field.multiple" :placeholder="fieldPlaceholder(field, '-- Sélectionnez une option --')"
          label="text" trackBy="value" searchContains="true"></Chosen>
        <!-- Autocomplete : le composant est nommé par la config (`component`) et doit être
             enregistré globalement, comme pour les filtres étendus d'AdminTableSync. Sert aux
             listes trop volumineuses pour un <select>. Les composants adossés à un référentiel
             (agence, maintenance externe) le chargent eux-mêmes et n'ont pas d'entrée
             `_options` ; OptionsAutocomplete, lui, travaille sur celle calculée par la vue. -->
        <component v-else-if="field.type == 'autocomplete'" :is="field.component" :id="field.name"
          v-model="form[field.name]" variant="form" :disabled="field.disabled"
          :options="field.options ? field.options($store, form) : options[field.name + '_options']"
          :placeholder="fieldPlaceholder(field, '-- Recherchez en saisissant les premiers caractères --')">
        </component>
        <v-select v-else-if="field.type == 'vueselect'" :inputId="field.name" v-model="form[field.name]"
          :options="field.options ? field.options($store, form) : options[field.name + '_options']"
          :multiple="field.multiple"
          :placeholder="fieldPlaceholder(field, '-- Recherchez une option en saisissant les premiers caractères --')"
          label="text" :reduce="item => item.value" @search="fetchVueSelectOptions"
          @search:focus="currentVueSelect = field.name" @search:blur="blurVueSelect">
        </v-select>
        <b-form-radio-group v-else-if="field.type == 'yesno'" v-model="form[field.name]" :options="[
          { value: true, text: 'Oui' },
          { value: false, text: 'Non' },
        ]" buttons button-variant="outline-primary" :required="field.required && !isRequiredWaived(field)" :disabled="field.disabled">
        </b-form-radio-group>
        <vue-tags-input v-else-if="field.type == 'tags'" :id="field.name" class="tags form-control" v-model="currentTag"
          :tags="form[field.name]" placeholder="Ajouter un tag" autocomplete="off" :autocomplete-items="filtredTags"
          @tags-changed="newTags => form[field.name] = newTags" />
        <div class="textarea-wrapper" v-else-if="field.type == 'textarea'">
          <b-form-textarea :id="field.name" v-model="form[field.name]" :required="field.required && !isRequiredWaived(field)" rows="3"
            max-rows="6" />
        </div>
        <datepicker v-else-if="field.type == 'date'" :id="field.name" input-class="form-control"
          v-model="form[field.name]" :required="field.required && !isRequiredWaived(field)" :format="'dd/MM/yyyy'" :monday-first="true"
          :language="fr" :clear-button="true"
          :disabledDates="typeof field.disabledDates == 'function' ? field.disabledDates(form) : field.disabledDates"
          clear-button-icon="fa fa-times fa-lg">
        </datepicker>
        <template v-else-if="field.type == 'file'">
          <div class="d-flex" v-if="form[field.name] && typeof form[field.name] !== 'object'">
            <b-form-input class="file-preview flex-fill" type="text" v-model="form[field.name]" disabled />
            <b-button variant="outline-secondary" @click="form[field.name] = null">
              <i class="far fa-trash-alt"></i>
            </b-button>
          </div>
          <b-form-file v-else :id="field.name" v-model="form[field.name]" placeholder="Sélectionnez un fichier..."
            drop-placeholder="Déposez un fichier ici..." :required="field.required && !isRequiredWaived(field)"></b-form-file>
        </template>
        <div class="group_repeater" :class="{ 'group_repeater--labeled': field.showFieldLabels }"
          v-else-if="field.type == 'group_repeater'">
          <!-- Les champs d'une ligne n'ont pas d'étiquette : sur un répéteur qui en aligne
               plusieurs, l'étiquette est donnée une fois en tête de colonne. Le bouton de
               suppression est repris ici, masqué, pour que les colonnes tombent juste sans
               avoir à deviner sa largeur. Réservé aux répéteurs dont les colonnes sont les
               mêmes d'une ligne à l'autre (pas de `inFormCondition`). -->
          <div class="field_group__header" v-if="field.showFieldLabels && form[field.name] && form[field.name].length">
            <div v-if="field.draggable == true" class="drag-handle"></div>
            <div v-for="(sub_field, sub_field_index) in field.fields" :key="'label_' + sub_field_index"
              class="flex-fill" :style="cellStyle(sub_field)">
              {{ sub_field.label }}<sup v-if="sub_field.required">*</sup>
            </div>
            <b-button variant="outline-secondary" class="invisible" tabindex="-1" aria-hidden="true">
              <i class="far fa-trash-alt"></i>
            </b-button>
          </div>
          <component :is="field.draggable == true ? 'draggable' : 'div'" :list="form[field.name]">
            <div class="field_group d-flex" v-for="(field_group, field_group_index) in form[field.name]"
              :key="field_group_index">
              <div v-if="field.draggable == true" class="drag-handle"><i class="far fa-grip-lines fa-lg"></i></div>
              <template v-for="(sub_field, sub_field_index) in field.fields"
                v-if="(sub_field.inFormCondition && sub_field.inFormCondition(form, field_group)) || !sub_field.inFormCondition">
                <b-form-select class="flex-fill" :style="cellStyle(sub_field)" :key="sub_field_index" v-if="sub_field.type == 'select'"
                  :id="sub_field.name" v-model="field_group[sub_field.name]"
                  :options="sub_field.options ? sub_field.options($store, form, field_group) : options[sub_field.name + '_options']"
                  :required="sub_field.required" :disabled="(!sub_field.options || !sub_field.options($store, form, field_group).length) && (!options[sub_field.name + '_options'] ||
                    !options[sub_field.name + '_options'].length)
                    ">
                  <template slot="first">
                    <option v-if="!options[sub_field.name + '_options'] ||
                      !options[sub_field.name + '_options'].length
                    " :value="null" disabled>
                      {{ sub_field.firstOption ? sub_field.firstOption : "Chargement ..." }}
                    </option>
                    <option v-else :value="null">-- Sélectionnez une option --</option>
                  </template>
                </b-form-select>
                <Chosen class="flex-fill" :style="cellStyle(sub_field)" :key="sub_field_index" v-else-if="sub_field.type == 'chosen'"
                  :id="sub_field.name" v-model="field_group[sub_field.name]"
                  :options="options[sub_field.name + '_options']" :disabled="!options[sub_field.name + '_options'] ||
                    !options[sub_field.name + '_options'].length
                    " :placeholder="sub_field.placeholder || '-- Sélectionnez une option --'" label="text"
                  trackBy="value" searchContains="true">
                </Chosen>
                <v-select class="flex-fill" :style="cellStyle(sub_field)" :key="sub_field_index" v-else-if="sub_field.type == 'vueselect'"
                  :inputId="sub_field.name" v-model="field_group[sub_field.name]"
                  :options="sub_field.options ? sub_field.options($store, form, field_group) : options[sub_field.name + '_options']"
                  :multiple="sub_field.multiple"
                  :placeholder="sub_field.placeholder || '-- Recherchez une option en saisissant les premiers caractères --'"
                  label="text" :reduce="item => item.value" @search="fetchVueSelectOptions"
                  @search:focus="currentVueSelect = sub_field.name" @search:blur="blurVueSelect">
                </v-select>
                <template v-else-if="sub_field.type == 'file'">
                  <b-form-input class="file-preview flex-fill" :style="cellStyle(sub_field)" :key="sub_field_index"
                    v-if="field_group[sub_field.name] && field_group.id" type="text"
                    v-model="field_group[sub_field.name]" disabled />
                  <b-form-file :key="sub_field_index" :style="cellStyle(sub_field)" v-else :id="sub_field.name" v-model="field_group[sub_field.name]"
                    :state="Boolean(field_group[sub_field.name])" placeholder="Sélectionnez un fichier..."
                    drop-placeholder="Déposez un fichier ici..." :required="sub_field.required"></b-form-file>
                </template>
                <datepicker :key="sub_field_index" :style="cellStyle(sub_field)" v-else-if="sub_field.type == 'date'" :id="sub_field.name"
                  v-model="form[sub_field.name]" :required="sub_field.required" :monday-first="true"
                  :format="'dd/MM/yyyy'" :timezone="'Europe/Paris'" :wrapper-class="flex - fill"
                  :input-class="'form-control'" :language="fr" :clear-button="true"
                  :disabledDates="typeof sub_field.disabledDates == 'function' ? sub_field.disabledDates(form) : sub_field.disabledDates"
                  clear-button-icon="fa fa-times fa-lg">
                </datepicker>
                <b-form-input class="flex-fill" :style="cellStyle(sub_field)" :key="sub_field_index" v-else :id="sub_field.name"
                  :type="sub_field.type" :step="sub_field.step || null" v-model="field_group[sub_field.name]"
                  :required="sub_field.required" :readonly="sub_field.readonly" :placeholder="sub_field.label" />
              </template>
              <b-button variant="outline-secondary" @click="deleteGroupRepeater(field.name, field_group_index)">
                <i class="far fa-trash-alt"></i>
              </b-button>
            </div>
          </component>
          <b-button variant="outline-secondary" @click="addGroupRepeater(field.name)"
            v-if="!field.disableAdd || field.disableAdd !== true">
            Ajouter un élément
            <i class="far fa-plus"></i>
          </b-button>
        </div>
        <div v-else-if="field.type == 'wysiwyg'">
          <vue-editor v-model="form[field.name]" :editorToolbar="customToolbar"></vue-editor>
        </div>
        <b-form-input v-else :id="field.name" :type="field.type" :step="field.step || null" :max="field.max || null"
          v-model="form[field.name]" :required="field.required && !isRequiredWaived(field)" />
      </b-form-group>
      <p>* Champs requis</p>
      <div class="form-submit">
        <b-button type="submit" variant="outline-primary" :disabled="inSaving">
          {{ inSaving ? "Enregistrement..." : "Enregistrer" }}
          <i v-if="!inSaving" class="fa fa-chevron-right"></i>
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { en, fr } from "vuejs-datepicker/dist/locale";
import Chosen from "@/components/Global/Chosen";
import draggable from 'vuedraggable'
import VueTagsInput from '@johmun/vue-tags-input';
import { VueEditor } from "vue2-editor";

export default {
  name: "AdminForm",
  components: {
    Datepicker,
    Chosen,
    draggable,
    VueTagsInput,
    VueEditor
  },
  props: ["config", "form", "isBusy", "options", "submit"],
  data() {
    return {
      fields: {},
      inSaving: false,
      en: en,
      fr: fr,
      currentVueSelect: null,
      vueSelectTimer: null,
      currentTag: "",
      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
      ]
    };
  },
  computed: {
    // Retourne la liste de tags proposés filtrés selon l'expression recherchée.
    // Tous les champs de tags n'ont pas de vocabulaire à proposer : la saisie des valeurs
    // d'un attribut de demande est libre, il n'y a rien à autocompléter.
    filtredTags() {
      return (this.options['tags_options'] || []).filter(i => {
        return i.text.toLowerCase().indexOf(this.currentTag.toLowerCase()) !== -1;
      });
    },
  },
  mounted() {

    // Chargement des champs à afficher dans le form depuis la config pour les passer au composant AdminForm
    this.fields = this.config.fields.filter((field) => field.inForm == true);
  },
  watch: {
    form: function (form) {
      // Dans le cas d'une modification, on vérifie que l'utilisateur en a bien la permission
      if (
        this.form.id &&
        this.$route.name == this.config.editRouteName &&
        (this.config.hideActionEditButton ||
          (this.config.editCondition && !this.config.editCondition(this.form)) ||
          (this.config.authFeature &&
            !this.$store.getters["auth/isAuthorized"](this.config.authFeature + "_edit")))
      ) {
        this.$toastr.error(
          "Vous ne possédez pas les autorisations nécessaires pour accéder à la page demandée"
        );
        this.$router.push({ name: this.config.listRouteName });
      }
    },
  },
  methods: {
    onSubmit() {
      this.inSaving = true;


      // Si une fonction de soumission custom est définie
      if (this.config.customSubmit == true) {
        this.$emit("submit:custom", this.form);
        return true;
      }

      // Si une fonction de validation du formulaire est définie dans la config, on l'exécute
      if (this.config.valideFormFunction && this.config.valideFormFunction(this.form) !== true) {
        this.$toastr.error(this.config.valideFormFunction(this.form));
        this.inSaving = false;
        return false;
      }

      // Validation custom pour les champs datepicker
      let validation = true;
      this.fields.forEach((field) => {
        if (
          field.type != "yesno" &&
          this.isFieldRequired(field) &&
          (typeof this.form[field.name] === "undefined" || !this.form[field.name] || (typeof this.form[field.name] === "string" && !this.form[field.name].trim()))
        ) {
          // Avant de considérer le champ comme non rempli, on vérifie quand même qu'il est bien affiché dans le form
          if (!field.inFormCondition || field.inFormCondition(this.form)) {
            this.$toastr.error("Veuillez renseigner le champ " + field.label);
            validation = false;
          }
        }
      });
      if (validation !== true) {
        this.inSaving = false;
        return false;
      }

      if (this.submit && typeof this.submit === "function") {
        if (this.submit() == true) {
          this.inSaving = false;
        }
      } else {
        // Edition
        if (this.form.id) {
          this.$store
            .dispatch(
              this.config.storeNamespace + "/" + (this.config.storeSaveItemAction || "saveItem"),
              this.config.submitFormatter ? this.config.submitFormatter(this.form, this) : this.form
            )
            .then((response) => {
              this.$emit("submit:update:success", response);
              if (this.config.useEditModal) {
                this.inSaving = false;
                this.$root.$emit("bv::hide::modal", "admin_modal_form");
              } else if (this.config.redirectAfterEditRouteName) {
                this.$router.push({
                  name  : this.config.redirectAfterEditRouteName,
                  params: this.config.redirectAfterEditRouteParams == 'id' ? {id: response.data.object.id} : {}
                });
              } else {
                this.$router.go(-1);
              }
            })
            .catch((error) => {
              this.$emit("submit:update:error", error);
              this.inSaving = false;
            });
        }
        // Création
        else {
          this.$store
            .dispatch(
              this.config.storeNamespace + "/addItem",
              this.config.submitFormatter ? this.config.submitFormatter(this.form, this) : this.form
            )
            .then((response) => {
              this.$emit("submit:create:success", response);
              if (this.config.useNewModal) {
                this.$root.$emit("bv::hide::modal", "admin_modal_form");
                this.inSaving = false;
              } else if (this.config.redirectAfterCreateRouteName) {
                this.$router.push({
                  name  : this.config.redirectAfterCreateRouteName,
                  params: this.config.redirectAfterCreateRouteParams == 'id' ? {id: response.data.object.id} : {}
                });
              } else {
                this.$router.push({ name: this.config.listRouteName });
              }
            })
            .catch((error) => {
              this.$emit("submit:create:error", error);
              this.inSaving = false;
            });
        }
      }
    },
    /**
     * Un champ est-il obligatoire dans l'état courant du formulaire ? `required` le fige,
     * `requiredCondition` le fait dépendre de la saisie — un code interne est exigé d'un
     * produit du parc, pas d'une location pour compte. L'astérisque suit les deux, comme la
     * validation à la soumission.
     *
     * @param   {object}   field  Configuration du champ
     * @returns {boolean}
     */
    isFieldRequired(field) {
      if (this.isRequiredWaived(field)) {
        return false;
      }
      return field.required === true || (!!field.requiredCondition && field.requiredCondition(this.form));
    },
    /**
     * L'obligation du champ est-elle levée par l'état du formulaire ? `requiredWaiver`
     * (config) lève d'un coup toutes les obligations — un produit qu'on arrête n'a plus à
     * compléter sa fiche — sauf celles des champs marqués `alwaysRequired`, sans lesquels
     * l'enregistrement lui-même échouerait.
     *
     * @param   {object}   field  Configuration du champ
     * @returns {boolean}
     */
    isRequiredWaived(field) {
      return (
        !field.alwaysRequired &&
        !!this.config.requiredWaiver &&
        this.config.requiredWaiver(this.form, this.$store) === true
      );
    },
    /**
     * Texte d'invite d'un champ. Une fonction quand il dépend de l'état du formulaire —
     * l'affaire invite à choisir une agence tant qu'aucune ne l'est, puis à saisir un
     * numéro — une chaîne sinon. Même convention que `disabledDates`.
     *
     * @param   {object}  field     Configuration du champ
     * @param   {string}  fallback  Invite par défaut du type de champ
     * @returns {string}
     */
    fieldPlaceholder(field, fallback) {
      let placeholder =
        typeof field.placeholder == "function" ? field.placeholder(this.form) : field.placeholder;
      return placeholder || fallback;
    },
    /**
     * Largeur d'une colonne de répéteur : `width` la fige (« 90px » pour une quantité,
     * par exemple), sinon les colonnes se partagent l'espace à parts égales.
     *
     * @param   {object}        sub_field  Configuration du sous-champ
     * @returns {object|null}              Style à appliquer, ou null
     */
    cellStyle(sub_field) {
      return sub_field.width ? { flex: "0 0 " + sub_field.width } : null;
    },

    /**
     * Supprime un item d'un champ de type 'group_repeater'
     * @param   {string}    field_name    Nom du champ de type 'group_repeater'
     * @param   {integer}   index         Index de l'item à supprimer
     */
    deleteGroupRepeater(field_name, index) {
      // Si le champ est requis, on s'assure qu'il reste au minimum 1 item
      if (
        this.config.fields.find((field) => field.name == field_name).required &&
        this.form[field_name].length == 1
      ) {
        this.$toastr.warning("Ce champ est requis et doit donc comporter au minimum un élément");
        return;
      }
      this.form[field_name].splice(index, 1);
    },
    /**
     * Ajoute un item dans un champ de type 'group_repeater'
     * @param   {string}    field_name    Nom du champ de type 'group_repeater'
     */
    addGroupRepeater(field_name) {
      let field = { ...this.form }[field_name];
      let field_config = this.config.fields.find((field) => field.name == field_name && field.inForm == true)
      let field_group = {};

      if (field_config.hasOwnProperty('addGroupCondition') && field_config.addGroupCondition(field_name, this.form, this) !== true) {
        field_config.hasOwnProperty('addGroupConditionError')
        this.$toastr.warning(field_config.addGroupConditionError);
        return
      }

      field_config.fields.forEach((field) => {
        field_group[field.name] = null;
      });
      field.push(field_group);
      this.form[field_name] = field;
    },
    /**
     * Lance la recherche des options d'un select Vue Select
     * @param {string} search Expression recherchée
     * @param {boolean} loading En attente
     */
    fetchVueSelectOptions(search, loading) {
      clearTimeout(this.vueSelectTimer)
      if (search.length >= 3) {
        this.vueSelectTimer = setTimeout(() => {
          this.$emit("fetchOptions", this.currentVueSelect, search, loading);
        }, 300);
      }
    },
    /**
     * Lance le reset du champ quand on quitte un Vue Select
     */
    blurVueSelect() {
      this.$emit("resetOptions", this.currentVueSelect);
      this.currentVueSelect = null
    }
  },
};
</script>

<style lang="scss">
.drag-handle {
  width: 20px;
  margin-right: 10px;
  padding-top: 14px;
  cursor: grab;
}

.ti-tag {
  background-color: #1AAE7C !important;
  font-size: 0.75em !important;
}

.vue-tags-input {
  width: 100%;
  max-width: 100% !important;
  border-color: #E9E9E9 !important;
  padding: 0 !important;
  height: auto;
  min-height: calc(1.5em + 0.75rem + 2px);
}

.ti-input {
  border: none !important;
  padding: 4px 7px;
}

.quillWrapper {
  .ql-editor {
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Global/AdminForm.vue